import { Directive, HostListener, inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DatetimerangeComponent } from '@common/components/form/datetimerange.component';
import { ListExcelExportFile, ListExcelExportRequest } from '@common/models/common/ListExcelExport.model';
import { FileDownloadService } from '@common/services/file-download.service';
import { currentUserSubject } from '@common/services/user.service';
import { isMobile } from '@common/utils/isMobile';
import { TranslateService } from '@ngx-translate/core';
import {
    GridComponent,
    GridDataResult,
    PageChangeEvent,
    RowArgs,
    SelectableSettings
} from '@progress/kendo-angular-grid';
import { DrawerComponent } from '@progress/kendo-angular-layout';
import { SortDescriptor } from '@progress/kendo-data-query';
import { BaseKendoListService } from 'app/singleton-services/base-kendo-list.service';
import { LastUpdateService } from 'app/singleton-services/last-update.service';
import { StateService } from 'app/singleton-services/state.service';
import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, finalize, first } from 'rxjs/operators';
import { UserModel } from '../models/User.model';
import { ModalService } from '../services/modal.service';
import { CommonController } from './common.controller';
import { GridColumnConfigController } from './grid-column-config-controller';
import { fitColumnsToGrid } from './grid-column-layout';
import { applyDateDefaultSort, isDateField } from './sort-utils';

@Directive()
export class BaseKendoList extends CommonController implements OnInit, OnDestroy {
    user: UserModel;
    currentUser$: Subscription;
    editMode: boolean;
    createMode: boolean;
    viewMode: boolean;
    cloneMode: boolean;
    printMode: boolean;
    filter: any;
    form: UntypedFormGroup;
    sortsArr: SortDescriptor[];
    pageObj: any;
    isBusy: boolean;
    title = 'default';
    excelfileName: string;
    pdfFileName: string;
    animationState = 'out';
    pagingOptions: any = { type: 'numeric', info: true, buttonCount: 3, pageSizes: [15, 30, 60] };
    itemsGridView: GridDataResult;
    gridData: any[] = [];
    initialFilterValues: any; // default filter values from ListDisplayModel FormGroup
    autoLoadData = true;
    drawerOpen = false;
    drawerState = false;
    gridHeight: number;
    selected: any[] = [];
    selectableSettings: SelectableSettings = {};
    module: string;
    isFilterApplied = false;

    applyStateFilter = true;

    @ViewChild('drawer') drawer: DrawerComponent;
    @ViewChild(GridComponent) grid: GridComponent;
    @ViewChildren(GridComponent) grids: QueryList<GridComponent>; // used for detail grids
    private resizeTimer: ReturnType<typeof setTimeout>;
    @ViewChildren(DatetimerangeComponent) dateTimeRangeComponents: QueryList<DatetimerangeComponent>;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public listQuery(filter: any) {}
    public getDefaultFilter() {}

    protected dialogService: ModalService;
    protected translateService: TranslateService;
    protected stateService: StateService;
    protected router: Router;
    protected route: ActivatedRoute;
    protected lastUpdateService: LastUpdateService;
    private queryParamsSubscription: Subscription;
    private savedGridScrollLeft: number = null;
    private readonly exportFileDownloadService = inject(FileDownloadService);

    constructor(baseKendoListService: BaseKendoListService) {
        super(baseKendoListService.getDialogService(), baseKendoListService.getTranslateService());
        this.dialogService = baseKendoListService.getDialogService();
        this.translateService = baseKendoListService.getTranslateService();
        this.stateService = baseKendoListService.getStateService();
        this.router = baseKendoListService.getRouter();
        this.route = baseKendoListService.getActivatedRoute();
        this.lastUpdateService = baseKendoListService.getLastUpdateService();

        this.currentUser$ = currentUserSubject.subscribe((user) => {
            this.user = user;
        });

        // user can't change page size when in mobile view
        if (isMobile()) {
            this.pagingOptions = { type: 'numeric', info: true, buttonCount: 3 };
        }

        this.calculateGridHeight(151);
        this.module = this.router.url.split('/').filter(Boolean).slice(0, 2).join('/') + '/take';
    }

    ngOnInit(): void {
        this.resetPaging();
        this.defaultSort();
        this.form = this.filter.createFormGroup();
        this.initialFilterValues = this.form.value;
        this.excelfileName = this.title + '.xlsx';
        this.pdfFileName = this.title + '.pdf';

        // Subscribe to query parameter changes (including browser back/forward navigation)
        this.queryParamsSubscription = this.route.queryParams.subscribe((params) => {
            // If the grid has already been initialized and data has been loaded
            if (this.itemsGridView && this.itemsGridView.data) {
                this.restoreStateFromUrl(params);
            }
        });

        if (this.stateService.filterState.has(this.getNormalizedRouteKey()) && this.applyStateFilter) {
            this.setStoredFilter();
            if (this.autoLoadData) this.initialSearchFromTop(this.form, this.listQuery(this.filter));
            this.form.markAsTouched();
        } else {
            // Check if there are filter parameters in the URL on initial load
            this.route.queryParams.pipe(first()).subscribe((params) => {
                const urlFilter = this.extractFiltersFromUrl(params);

                if (urlFilter) {
                    // If URL contains filters, use them
                    this.filter = this.getDefaultFilter();

                    // Safely assign URL filter values
                    Object.keys(urlFilter).forEach((key) => {
                        const value = urlFilter[key];

                        // Special handling for array fields
                        if (key.includes('Ids') || key.includes('ids')) {
                            if (Array.isArray(value)) {
                                this.filter[key] = value;
                            } else if (value === null || value === undefined || value === 0 || value === '') {
                                this.filter[key] = [];
                            } else {
                                this.filter[key] = [value];
                            }
                        } else {
                            this.filter[key] = value;
                        }
                    });

                    this.form.patchValue(urlFilter);

                    // Handle sorting from URL
                    if (params['sortField'] && params['sortDir']) {
                        this.sortsArr = [{ field: params['sortField'], dir: params['sortDir'] }];
                    }

                    // Handle pagination from URL
                    if (params['page']) {
                        const page = parseInt(params['page'], 10) - 1;
                        const skip = page * this.pageObj.take;
                        this.pageObj.skip = skip;
                        this.filter.offset = skip;
                    }

                    // Handle page size from URL
                    if (params['pageSize']) {
                        const urlPageSize = parseInt(params['pageSize'], 10);
                        if ([15, 30, 60].includes(urlPageSize)) {
                            this.pageObj.take = urlPageSize;
                            this.filter.pageSize = urlPageSize;
                            // Also update localStorage
                            localStorage.setItem(this.module, urlPageSize.toString());
                            // Recalculate skip if page was also specified
                            if (params['page']) {
                                const page = parseInt(params['page'], 10) - 1;
                                const skip = page * urlPageSize;
                                this.pageObj.skip = skip;
                                this.filter.offset = skip;
                            }
                        }
                    } else {
                        // Check localStorage for page size if not in URL
                        const storedTake = localStorage.getItem(this.module);
                        if (storedTake) {
                            const parsedTake = parseInt(storedTake, 10);
                            if ([15, 30, 60].includes(parsedTake)) {
                                this.pageObj.take = parsedTake;
                                this.filter.pageSize = parsedTake;
                                // Recalculate skip if page was also specified
                                if (params['page']) {
                                    const page = parseInt(params['page'], 10) - 1;
                                    const skip = page * parsedTake;
                                    this.pageObj.skip = skip;
                                    this.filter.offset = skip;
                                }
                            }
                        }
                    }

                    if (this.autoLoadData) this.initialSearchFromTop(this.form, this.listQuery(this.filter));
                    this.form.markAsTouched();
                } else {
                    // No URL filters, use defaults
                    this.filter = this.getDefaultFilter();
                    if (this.autoLoadData) this.searchFromTop(this.form, this.listQuery(this.filter));
                }
            });
        }
    }

    ngOnDestroy(unsubArray?: Subscription[]): void {
        clearTimeout(this.resizeTimer);
        this.currentUser$.unsubscribe();
        if (this.queryParamsSubscription) {
            this.queryParamsSubscription.unsubscribe();
        }
        if (unsubArray) {
            unsubArray.forEach((x) => x.unsubscribe());
        }
    }

    @HostListener('window:resize')
    onWindowResize() {
        this.calculateGridHeight(151);
        // the last column takes the leftover width, so a different grid width needs a new layout
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
            if (this.grid?.wrapper?.nativeElement.offsetWidth) {
                this.refreshColumnLayout();
            }
        }, 150);
    }

    toggleFilter() {
        this.animationState = this.animationState === 'out' ? 'in' : 'out';
    }

    toggleDrawer() {
        this.drawerOpen = !this.drawerOpen;
    }

    toggleSection(section: string, event?: Event) {
        if (event) {
            event.preventDefault();
        }
        this[section] = !this[section];
    }

    setDrawerState(drawerState: boolean): void {
        this.drawerState = drawerState;
    }

    // PAGE CHANGE
    protected resetPaging(take: number = 15, skip: number = 0): void {
        this.route.queryParams
            .subscribe((params) => {
                // Preserve page size from URL or localStorage, but always reset to page 1 (skip=0)
                if (params['pageSize']) {
                    const urlPageSize = parseInt(params['pageSize'], 10);
                    if ([15, 30, 60].includes(urlPageSize)) {
                        take = urlPageSize;
                    }
                } else {
                    // If no URL page size, check localStorage
                    const storedTake = localStorage.getItem(this.module);
                    if (storedTake) {
                        const parsedTake = parseInt(storedTake, 10);
                        if ([15, 30, 60].includes(parsedTake)) {
                            take = parsedTake;
                        }
                    }
                }
            })
            .unsubscribe();
        this.pageObj = { take, skip, totalElements: 0 };
    }

    /**
     * Restores pagination and filter state from URL parameters
     */
    private restoreStateFromUrl(params: any): void {
        let stateChanged = false;

        // Restore page size first - URL has priority, then localStorage
        let take = this.pageObj.take;
        if (params['pageSize']) {
            const urlPageSize = parseInt(params['pageSize'], 10);
            if (urlPageSize !== take && [15, 30, 60].includes(urlPageSize)) {
                take = urlPageSize;
                this.pageObj.take = take;
                this.filter.pageSize = take;
                stateChanged = true;
            }
        } else {
            // Check localStorage if no URL page size
            const storedTake = localStorage.getItem(this.module);
            if (storedTake) {
                const parsedTake = parseInt(storedTake, 10);
                if (parsedTake !== take && [15, 30, 60].includes(parsedTake)) {
                    take = parsedTake;
                    this.pageObj.take = take;
                    this.filter.pageSize = take;
                    stateChanged = true;
                }
            }
        }

        // Restore pagination
        const page = params['page'] ? parseInt(params['page'], 10) - 1 : 0;
        const skip = page * take;

        if (skip !== this.pageObj.skip) {
            this.pageObj.skip = skip;
            this.filter.offset = skip;
            this.filter.pageSize = take;
            stateChanged = true;
        }

        // Restore filters from URL
        const urlFilter = this.extractFiltersFromUrl(params);
        if (urlFilter && this.hasFilterChanged(urlFilter)) {
            // Update form with URL filter values
            this.form.patchValue(urlFilter);

            // Update internal filter object safely
            Object.keys(urlFilter).forEach((key) => {
                const value = urlFilter[key];

                // Special handling for array fields
                if (key.includes('Ids') || key.includes('ids')) {
                    if (Array.isArray(value)) {
                        this.filter[key] = value;
                    } else if (value === null || value === undefined || value === 0 || value === '') {
                        this.filter[key] = [];
                    } else {
                        this.filter[key] = [value];
                    }
                } else {
                    this.filter[key] = value;
                }
            });

            // Update sorting if present in URL
            if (params['sortField'] && params['sortDir']) {
                this.sortsArr = [{ field: params['sortField'], dir: params['sortDir'] }];
            }

            stateChanged = true;
        }

        // Only reload data if state actually changed
        if (stateChanged) {
            this.isBusy = true;
            this.getQueryAction(this.getQueryBuilder(false, this.form, this.listQuery));
        }
    }

    /**
     * Extracts filter values from URL parameters
     */
    private extractFiltersFromUrl(params: any): any {
        const urlFilter = {};
        const excludedParams = ['page', 'pageSize', 'sortField', 'sortDir', 'tab'];

        Object.keys(params).forEach((key) => {
            if (!excludedParams.includes(key) && params[key] !== null && params[key] !== undefined) {
                // Special handling for array fields (fields ending with 'Ids' or 'ids')
                if (key.includes('Ids') || key.includes('ids')) {
                    // Array fields should be handled as arrays
                    if (Array.isArray(params[key])) {
                        urlFilter[key] = params[key].map((value: any) => this.normalizeIdValue(value));
                    } else if (params[key] === '0' || params[key] === 0) {
                        urlFilter[key] = [];
                    } else {
                        // Single value in URL parameter, convert to array
                        urlFilter[key] = [this.normalizeIdValue(params[key])];
                    }
                } else {
                    // Handle different parameter types for non-array fields
                    if (params[key] === 'true' || params[key] === 'false') {
                        urlFilter[key] = params[key] === 'true';
                    } else if (!isNaN(params[key]) && params[key] !== '') {
                        urlFilter[key] = Number(params[key]);
                    } else {
                        urlFilter[key] = params[key];
                    }
                }
            }
        });

        return Object.keys(urlFilter).length > 0 ? urlFilter : null;
    }

    private normalizeIdValue(value: any): any {
        if (typeof value === 'number') {
            return value;
        }
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed === '') {
                return value;
            }
            const numericValue = Number(trimmed);
            if (!Number.isNaN(numericValue)) {
                return numericValue;
            }
        }
        return value;
    }

    /**
     * Checks if the filter from URL is different from current form values
     */
    private hasFilterChanged(urlFilter: any): boolean {
        const currentFormValues = this.form.value;

        // Check if any URL filter value is different from current form value
        for (const key in urlFilter) {
            if (urlFilter[key] !== currentFormValues[key]) {
                return true;
            }
        }

        return false;
    }

    /**
     * Updates the URL with current filter and pagination state
     */
    private updateUrlWithState(): void {
        this.route.queryParams.pipe(first()).subscribe((currentParams) => {
            const currentPage = Math.floor(this.pageObj.skip / this.pageObj.take) + 1;
            const queryParams: any = {};

            // Preserve tab parameter if it exists
            if (currentParams['tab']) {
                queryParams.tab = currentParams['tab'];
            }

            // Add pagination
            if (currentPage > 1) {
                queryParams.page = currentPage;
            }

            // Add page size if different from default (15)
            if (this.pageObj.take !== 15) {
                queryParams.pageSize = this.pageObj.take;
            }

            // Add sorting
            if (this.sortsArr && this.sortsArr.length > 0 && this.sortsArr[0].field !== 'id') {
                queryParams.sortField = this.sortsArr[0].field;
                queryParams.sortDir = this.sortsArr[0].dir;
            }

            // Add filters (exclude internal properties and default values)
            const formValues = this.form.value;
            const defaultValues = this.initialFilterValues;

            Object.keys(formValues).forEach((key) => {
                const value = formValues[key];
                const defaultValue = defaultValues[key];

                // Only add to URL if value is different from default and not empty
                if (value !== defaultValue && value !== null && value !== undefined && value !== '') {
                    queryParams[key] = value;
                }
            });

            // Update URL without triggering navigation
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: queryParams,
                replaceUrl: true
            });
        });
    }

    protected internalPageChange(event: PageChangeEvent, pageObj: any, searchCallback: any) {
        this.filter.offset = event.skip;
        this.filter.pageSize = event.take;
        pageObj.skip = event.skip;
        pageObj.take = event.take;

        // Save page size to localStorage when changed
        if (event.take !== parseInt(localStorage.getItem(this.module) || '15', 10)) {
            localStorage.setItem(this.module, event.take.toString());
        }

        // Update URL with new pagination state
        this.updateUrlWithState();

        searchCallback();
    }

    public pageChange(event: PageChangeEvent): void {
        this.internalPageChange(event, this.pageObj, this.search.bind(this, this.form, this.listQuery(this.filter)));
    }

    /**
     * "Export All Data" built on the server, for lists too big for the grid's own Excel export (Kendo
     * builds the file in the browser at about a second per thousand rows). Sends the current filter with
     * its sort and the visible columns in their order, then saves the returned workbook.
     */
    protected exportExcelFromServer(
        columnConfig: GridColumnConfigController,
        exportRequest: (body: ListExcelExportRequest) => Observable<ListExcelExportFile>,
        fileName: string
    ): void {
        const columns = columnConfig.visibleColumnsInOrder().map((column) => ({
            key: column.key,
            title: this.translateService.instant(column.title)
        }));

        this.isBusy = true;
        exportRequest({ filter: { ...this.filter, offset: 0, pageSize: 0 }, columns })
            .pipe(
                first(),
                finalize(() => (this.isBusy = false))
            )
            .subscribe((file) => this.exportFileDownloadService.handleAttachmentDownload({ ...file, name: fileName }));
    }

    // report data
    getAllPages: any = () => {
        const tempFilter = { ...this.filter };
        tempFilter.pageSize = 0;
        return this.listQuery(tempFilter);
    };

    // SORT
    protected defaultSort() {
        this.sortsArr = [{ field: 'id', dir: 'desc' }];
    }

    protected internalSortChange(sort: SortDescriptor[], searchCallback: any) {
        this.sortsArr = sort;

        // Update URL with new sort state
        this.updateUrlWithState();

        searchCallback();
    }

    /**
     * Override this method in a subclass to declare additional date fields that should
     * sort DESC by default. Call super.isDateSortField(field) to retain the default logic.
     */
    protected isDateSortField(field: string): boolean {
        return isDateField(field);
    }

    public sortChange(sort: SortDescriptor[]): void {
        const adjusted = applyDateDefaultSort(sort, this.sortsArr, (f) => this.isDateSortField(f));
        this.internalSortChange(adjusted, this.search.bind(this, this.form, this.listQuery(this.filter)));
    }

    // SEARCH
    searchFromTop(form, listQuery) {
        this.resetPaging();
        // Update URL immediately to reflect page reset
        this.updateUrlWithState();
        this.search(form, listQuery);
    }

    search(form, listQuery) {
        this.isBusy = true;
        this.getQueryAction(this.getQueryBuilder(false, form, listQuery));

        if (this.applyStateFilter)
            // Update URL with current filter and pagination state
            this.updateUrlWithState();
    }

    // used for search from html
    searchForm() {
        this.resetPaging();
        // Clear page parameter from URL before searching
        this.route.queryParams.pipe(first()).subscribe((params) => {
            const preservedParams: any = {};
            if (params['tab']) {
                preservedParams.tab = params['tab'];
            }
            // Preserve pageSize if it exists and is not default
            if (params['pageSize'] && params['pageSize'] !== '15') {
                preservedParams.pageSize = params['pageSize'];
            }

            this.router
                .navigate([], {
                    relativeTo: this.route,
                    queryParams: preservedParams,
                    replaceUrl: true
                })
                .then(() => {
                    this.search(this.form, this.listQuery(this.filter));
                });
        });
        this.isFilterApplied = true;
    }

    // search when we have the filter stored in state service
    // NOTE: paging is intentionally NOT reset here - setStoredFilter() has already
    // restored pageObj from the stored filter so returning to the list (e.g. via the
    // Back button) lands on the page the user left, even when the URL carries no page param.
    initialSearchFromTop(form, listQuery) {
        this.isBusy = true;
        this.getQueryAction(this.getQueryBuilder(true, form, listQuery));
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private getQueryBuilder(initialSearch: boolean = false, form: any, listQuery: any): any {
        this.filter.offset = this.pageObj.skip;
        this.filter.pageSize = this.pageObj.take;

        for (const order of this.sortsArr) {
            if (order['dir']) {
                this.filter.orderBy = order['field'];
                this.filter.orderDirection = order['dir'];
            }
        }

        // Safely assign form values to filter, preserving array types
        const formValues = form.value;
        Object.keys(formValues).forEach((key) => {
            const value = formValues[key];

            // Special handling for array fields that should remain as arrays
            if (key.includes('Ids') || key.includes('ids')) {
                // Ensure array fields remain as arrays, even if they have unexpected values
                if (Array.isArray(value)) {
                    this.filter[key] = value;
                } else if (value === null || value === undefined || value === 0 || value === '') {
                    this.filter[key] = [];
                } else {
                    // Convert single values to arrays for consistency
                    this.filter[key] = [value];
                }
            } else {
                this.filter[key] = value;
            }
        });
        if (this.applyStateFilter) {
            this.stateService.updateFilter(this.getNormalizedRouteKey(), this.filter);
        }
        this.lastUpdateService.setLastUpdatedTime(); // setting last time data has been updated

        return this.listQuery(this.filter);
    }

    getQueryAction(query: Observable<any>) {
        this.captureGridScrollLeft();

        query
            .pipe(
                first(),
                catchError((err) => {
                    this.savedGridScrollLeft = null;
                    this.isBusy = false;
                    return throwError(err);
                })
            )
            .subscribe((x: any) => {
                this.itemsGridView = {
                    data: x.data,
                    total: x.count
                };

                this.gridData = this.itemsGridView.data; // used in some cases where we have in table filtering
                setTimeout(() => {
                    if (this.grid && this.grid.columns) {
                        this.expandLastColumnToFit();
                    }
                    // autofit applies the recalculated column widths on the next change detection
                    // pass, so the scroll position can only be put back after that
                    setTimeout(() => this.restoreGridScrollLeft());
                });

                this.isBusy = false;
            });
    }

    // getting stored filter data when returning to page
    public setStoredFilter() {
        if (!this.applyStateFilter) return;
        this.filter = this.stateService.getFilter(this.getNormalizedRouteKey());

        this.sortsArr = [{ field: this.filter.orderBy, dir: this.filter.orderDirection }] as SortDescriptor[];

        // Restore pagination from the stored filter so returning to the list lands on the
        // same page the user left. This is independent of the URL query params, which are
        // dropped when the Back button navigates via an explicit route.
        if (this.filter.pageSize) {
            this.pageObj.take = this.filter.pageSize;
        }
        this.pageObj.skip = this.filter.offset || 0;

        this.form.patchValue(this.filter);
        return this.filter;
    }

    // CLEAR
    clearFilter() {
        this.form.reset(this.initialFilterValues);
        this.defaultSort();
        this.filter = this.getDefaultFilter();
        if (this.dateTimeRangeComponents) this.clearAllDateRanges();

        // Reset pagination to first page
        this.pageObj.skip = 0;
        this.filter.offset = 0;

        if (this.applyStateFilter) this.stateService.clearFilter(this.getNormalizedRouteKey());

        // Perform search first
        this.searchFromTop(this.form, this.listQuery(this.filter));
        this.isFilterApplied = false;
        this.form.markAsPristine();

        // Clear URL parameters except tabs (after search to override any URL updates)
        this.route.queryParams.pipe(first()).subscribe((params) => {
            const preservedParams: any = {};
            if (params['tab']) {
                preservedParams.tab = params['tab'];
            }

            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: preservedParams,
                replaceUrl: true
            });
        });

        // Perform search
        this.isBusy = true;
        this.getQueryAction(this.getQueryBuilder(false, this.form, this.listQuery(this.filter)));
        this.isFilterApplied = false;
        this.form.markAsPristine();
    }

    isFilterClear(filter: any): boolean {
        // Create a copy and remove internal properties
        const filterCopy = { ...filter };
        delete filterCopy.offset;
        delete filterCopy.orderBy;
        delete filterCopy.orderDirection;
        delete filterCopy.pageSize;
        delete filterCopy.selectAllButton;

        // Compare the remaining filter values with initial values
        return JSON.stringify(filterCopy) === JSON.stringify(this.initialFilterValues);
    }

    belongsToOrganization(organization: any): boolean {
        const user = currentUserSubject.getValue();
        if (!user || !organization) {
            return false;
        }
        return user.belongsToOrganization(organization);
    }

    public onRowClickEditItem(dataItem: any): void {
        this.router.navigate([this.router.url.replace(/list/g, 'edit'), dataItem]);
    }

    public calculateGridHeight(heightOfElements: number): void {
        this.gridHeight = isMobile() ? null : window.innerHeight - heightOfElements;
    }

    setSelectableSettings(): void {
        this.selectableSettings = {
            checkboxOnly: false,
            mode: 'multiple'
        };
    }

    selectionCallback(context: RowArgs) {
        return context.dataItem.id;
    }

    // check currently selected rows when switching pages
    isRowSelected = (e: RowArgs) => this.selected.indexOf(e.dataItem.id) >= 0;

    // angular upgrade fix
    keyChange(e: any) {
        this.selected = e;
    }

    clearAllDateRanges() {
        this.dateTimeRangeComponents.forEach((dateTimeRangeComponent) => {
            dateTimeRangeComponent.clearRange();
        });
    }

    expandDetailGrid() {
        setTimeout(() => {
            const allGrids = this.grids.toArray();
            const latestDetailGrid = allGrids[allGrids.length - 1];
            if (latestDetailGrid?.columnList) {
                this.expandLastColumnToFit(latestDetailGrid);
            }
        });
    }

    // only the horizontal position is kept - scrolling back to the top is the expected behaviour
    // once the row order or the page changed.
    private getGridScrollContainer(): HTMLElement {
        return this.grid?.wrapper?.nativeElement?.querySelector('.k-grid-content') ?? null;
    }

    private captureGridScrollLeft(): void {
        const container = this.getGridScrollContainer();

        // when the grid is already out of the DOM (a reload is in flight, or it was never
        // rendered) there is nothing to read, so keep whatever was captured before
        if (container) {
            this.savedGridScrollLeft = container.scrollLeft;
        }
    }

    private restoreGridScrollLeft(): void {
        const container = this.getGridScrollContainer();

        if (container && this.savedGridScrollLeft) {
            container.scrollLeft = this.savedGridScrollLeft;
        }

        this.savedGridScrollLeft = null;
    }

    /**
     * Re-fits the columns to their content and hands the leftover grid width to the last column on screen.
     * The same routine runs after every data load; call it after anything else that changes which
     * columns are shown, their order or the grid width, so the layout never depends on paging.
     */
    refreshColumnLayout(): void {
        if (this.grid && this.grid.columns) {
            this.expandLastColumnToFit();
        }
    }

    protected expandLastColumnToFit(grid: GridComponent = this.grid) {
        fitColumnsToGrid(grid);
    }

    // get normalized route key for state service
    protected getNormalizedRouteKey(): string {
        const url = this.router.url.split('?')[0]; // base path
        const queryParams = this.route.snapshot.queryParams;

        // Only preserve the tab param if present
        const preservedParams = [];
        if (queryParams['tab'] !== undefined) {
            preservedParams.push(`tab=${queryParams['tab']}`);
        }

        return preservedParams.length ? `${url}?${preservedParams.join('&')}` : url;
    }
}
